import { cx } from './cx.ts'
import { statusKind, statusSeverity, type StatusKind, type StatusSeverity } from './statusKind.ts'
import type { StatusTone } from './statusTone.ts'

import './badge.css'

/**
 * 상태 배지 — 와이어프레임 `stBadge()` 1063행.
 *
 * 🔴 색을 쓰지 않는다. 점 모양(● 채움 · ○ 빈 · ◐ 반채움 · ■ 네모)과 선 스타일,
 *    반전으로 가른다(§5-4). 어느 상태에서도 **높이와 도형이 같다**(§1-2).
 *    ⚠ `tone` 은 그 위에 «얹는» 옅은 면이다(2026-09-30 · 레시피 상태만) — 점 · 글자는 그대로다.
 * 🔴 **콘솔 스킨은 모양 기호를 이상 상태에만 둔다**(디자인팀 시안 2026-10-01 · `docs/design/CTcells-console.html`
 *    `.pill--warn::before` · `.pill--err::before`). 정상(진행 중 · 완료 · 대기)은 글자만 있는 알약이고,
 *    주의(▲ = 일시정지 · 개입 대기 · 복구 중)와 위험(⯃! = 오류 · 폐기)만 표지(`.a-stb__mark`)가 선다.
 *    DOM 은 스킨과 무관하게 같다 — 점 · 글리프 · 표지가 다 있고 **어느 것이 보이는지는 토큰**
 *    (`scale.css` `--stb-dot-display` · `--stb-mark-display`)이 가른다. 흑백 앱 넷은 점 · 글리프 그대로다
 *    (색이 없어 점 모양이 상태를 말한다). 원자는 `[data-skin]` 으로 분기하지 않는다(`atomsCss.test.ts`).
 *
 * 🔴 `label` 이 필수인 이유: 원값이 batch · recipe · sample · module 여러 도메인에서 온다.
 *    원자가 도메인을 추측하면 영문 원값이 화면에 샌다(§1-1 "enum 영문 노출 금지").
 *    호출부에서 `@ctcells/i18n` 의 `batchStatusLabel()` 등을 거쳐서 넘겨라.
 */
export type StatusBadgeProps = {
  /** 원값(RUNNING · running · draft …). 표기 종류 매핑에만 쓴다 */
  status: string
  /** 화면 표기(한글). 필수 */
  label: string
  /**
   * 기본 'sm'. `pill` = 작업 상태 칩 단(디자인팀 시안 작업 이력 `.dt .pill` · eric 2026-10-29) — 작업 목록 · 이력 · 상세 머리 · 정지 팝업.
   * 값은 콘솔 토큰(`--stb-pill-*`)이라 흑백 앱은 `sm` 과 같다.
   */
  size?: 'sm' | 'lg' | 'pill' | undefined
  /**
   * 툴팁에 넣을 원 이름. 화면 표기와 정본 값이 다를 때만 쓴다.
   * 예: READY → 'WAITING_FOR_MANUAL_LOAD · 개입 대기' (와이어프레임 1063행)
   */
  originalValue?: string | undefined
  /**
   * 옅은 바탕 톤. 🔴 **호출부가 도메인 표(`statusTone.ts`)에서 골라 넘긴다** — 원자는 원값만으로 톤을 짓지 않는다.
   * 안 넘기면 종전 모양 그대로다(작업 · 알람 · 시료 · 모듈 배지).
   */
  tone?: StatusTone | undefined
  /**
   * 테두리 없는 알약 — 면 · 글자는 그대로, 테만 투명(바탕이 테 자리까지 깔려 높이가 안 바뀐다).
   * 시료 상태 칩이 쓴다(eric 2026-10-29 「시료쪽도.. 테두리 빼고」). 안 넘기면 종전 테 그대로다.
   */
  flat?: boolean | undefined
}

/**
 * 점으로 표현되지 않는 상태는 기호를 쓴다 — 와이어프레임 ::before 내용 그대로.
 * `recovery` 는 와이어프레임에 없던 칸이다(정본 `06 §4.2a` 6분류 중 「복구 중」) —
 * 「이상」(반전 ▲)으로 접으면 안전정지≠고장 구분처럼 **복구 중≠고장**이 죽는다.
 */
const GLYPH: Partial<Record<StatusKind, string>> = {
  err: '▲',
  pause: '❙❙',
  recovery: '↻',
}

/**
 * 콘솔 스킨 표지 — 시안의 `.pill--warn` · `.pill--err` 마스크 경로를 그대로 인라인 SVG 로 옮겼다(16 상자).
 * `Icon` 을 안 쓰는 이유: 그쪽은 24 상자 선 그림이고 크기가 `--icon-*` 이다. 표지는 채운 도형이고
 * **배지 글자에 비례**(em)해야 높이를 안 밀어낸다.
 */
const MARK: Partial<Record<StatusSeverity, React.ReactNode>> = {
  warn: <path d="M8 1.8 15 14H1Z" />,
  err: (
    <path
      d="M5.1 1h5.8L15 5.1v5.8L10.9 15H5.1L1 10.9V5.1ZM7.2 4h1.6v5H7.2Zm0 6.3h1.6v1.6H7.2Z"
      fillRule="evenodd"
    />
  ),
}

export function StatusBadge({
  status,
  label,
  size = 'sm',
  originalValue,
  tone,
  flat = false,
}: StatusBadgeProps) {
  const kind = statusKind(status)
  const glyph = GLYPH[kind]
  // 🔴 작업 「중단」만 종류(위험)와 다른 주의(▲)다 — `statusSeverity` 머리말
  const severity = statusSeverity(status)
  const mark = MARK[severity]

  return (
    <span
      className={cx(
        'a-stb',
        `a-stb--${kind}`,
        size === 'lg' && 'a-stb--lg',
        size === 'pill' && 'a-stb--pill',
        tone !== undefined && `a-stb--tone-${tone}`,
        flat && 'a-stb--flat',
      )}
      data-severity={severity}
      data-status={status}
      title={originalValue}
    >
      {glyph === undefined ? (
        <i aria-hidden="true" className="a-stb__dot" />
      ) : (
        <i aria-hidden="true" className="a-stb__glyph">
          {glyph}
        </i>
      )}
      {mark !== undefined && (
        <svg
          aria-hidden="true"
          className={`a-stb__mark a-stb__mark--${severity}`}
          fill="currentColor"
          focusable="false"
          viewBox="0 0 16 16"
        >
          {mark}
        </svg>
      )}
      {/* 글자는 제 상자에 선다 — 쓰는 쪽이 배지 폭을 묶으면(공정 칸 · 작업 줄) 글자만 …로 잘리고 전문은 `title` 이 말한다 */}
      <span className="a-stb__label">{label}</span>
    </span>
  )
}
